import axios from 'axios';

const API_BASE_URL = '/api/v1';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor for auth
apiClient.interceptors.request.use(
  (config) => {
    // Add API key if available
    const apiKey = localStorage.getItem('kredit_api_key');
    if (apiKey) {
      config.headers['X-API-Key'] = apiKey;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized
      localStorage.removeItem('kredit_api_key');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export interface DashboardSummary {
  period: {
    start_date: string;
    end_date: string;
    days: number;
  };
  summary: {
    total_requests: number;
    avg_processing_time_seconds: number;
    requests_per_day: number;
  };
  decisions: {
    [key: string]: number;
  };
  daily_trend: {
    date: string;
    requests: number;
  }[];
}

export interface ProfilingRequest {
  id: string;
  simulation_id: string;
  decision_status: string;
  decision_reason: string;
  created_at: string;
  processing_time_seconds: number;
  customer_type: string;
  credit_score: number;
  ai_confidence: number;
  ai_recommendation: string;
}

export interface RequestDetails extends ProfilingRequest {
  updated_at: string;
  experian_data: any;
  truora_data: any;
  validations: {
    [key: string]: {
      status: string;
      details: any;
      created_at: string;
    };
  };
  total_validations: number;
}

export interface AIvsRulesComparison {
  period: {
    start_date: string;
    end_date: string;
    days: number;
  };
  summary: {
    total_analyzed: number;
    agreement_rate: number;
    disagreement_rate: number;
  };
  breakdown: {
    agreements: number;
    disagreements: number;
    ai_more_conservative: number;
    ai_more_liberal: number;
  };
  cases: {
    request_id: string;
    simulation_id: string;
    final_decision: string;
    ai_recommendation: string;
    ai_confidence: number;
    created_at: string;
    agreement: boolean;
    disagreement_type?: string;
  }[];
}

export interface RejectionReason {
  reason: string;
  count: number;
  percentage: number;
}

export interface ValidationStepPerformance {
  [stepName: string]: {
    total: number;
    passed: number;
    failed: number;
    warning: number;
    pass_rate: number;
    fail_rate: number;
    warning_rate: number;
  };
}

export interface HealthResponse {
  status: string;
  version?: string;
  timestamp?: string;
  services?: Record<string, string>;
}

export interface ProfilingTriggerPayload {
  simulation_id: string;
  cedula?: string;
  trigger_source?: string;
  priority?: 'normal' | 'high';
}

export interface ProfilingAnalyzePayload {
  simulation_id?: string;
  cedula?: string;
  customer_type?: 'pensionado' | 'docente';
  force_refresh?: boolean;
  documents?: OcrDocument[];
}

export interface ProfilingAnalyzeWithDocsPayload {
  documents: OcrDocument[];
  cedula?: string;
  customer_type?: 'pensionado' | 'docente';
}

export interface ProfilingStatusResponse {
  profiling_id: string;
  simulation_id: string;
  status: string;
  progress_percentage: number;
  current_step?: string;
  created_at: string;
}

export interface ProfilingResultResponse {
  profiling_id: string;
  simulation_id: string;
  decision: string;
  decision_reason?: string;
  validations: Record<string, unknown>;
  score_analysis?: Record<string, unknown>;
  credit_limits?: Record<string, unknown>;
  documents_required?: string[];
  analyst_notes?: string;
  created_at: string;
  updated_at: string;
}

export interface PendingReviewItem {
  profiling_id: string;
  simulation_id: string;
  customer_name?: string;
  customer_document?: string;
  customer_type?: string;
  created_at: string;
  pending_validations?: string[];
}

export interface AnalystReviewPayload {
  analyst_id: string;
  analyst_name: string;
  review_notes: string;
  final_decision: 'APPROVED' | 'REJECTED' | 'PENDING_REVIEW' | 'PROCESSING';
}

// API Functions
export const dashboardAPI = {
  getSummary: (days: number = 30): Promise<DashboardSummary> =>
    apiClient.get(`/dashboard/stats/summary?days=${days}`).then(res => res.data),

  getRecentRequests: (limit: number = 50, status?: string): Promise<{ requests: ProfilingRequest[]; total_returned: number }> =>
    apiClient.get(`/dashboard/requests?limit=${limit}${status ? `&status=${status}` : ''}`).then(res => res.data),

  getRequestDetails: (requestId: string): Promise<{ request: RequestDetails; validations: any; total_validations: number }> =>
    apiClient.get(`/dashboard/requests/${requestId}/details`).then(res => res.data),

  getAIvsRulesComparison: (days: number = 30): Promise<AIvsRulesComparison> =>
    apiClient.get(`/dashboard/analysis/ai-vs-rules?days=${days}`).then(res => res.data),

  getTopRejectionReasons: (days: number = 30, limit: number = 10): Promise<{ period: any; total_rejections: number; top_reasons: RejectionReason[] }> =>
    apiClient.get(`/dashboard/analysis/top-rejection-reasons?days=${days}&limit=${limit}`).then(res => res.data),

  getValidationStepPerformance: (days: number = 30): Promise<{ period: any; validation_steps: ValidationStepPerformance }> =>
    apiClient.get(`/dashboard/analysis/validation-step-performance?days=${days}`).then(res => res.data),

  getModelInfo: (): Promise<{ model: any; timestamp: string }> =>
    apiClient.get('/dashboard/model-info').then(res => res.data),

  getRestrictiveListsInfo: (): Promise<{ lists: any; timestamp: string }> =>
    apiClient.get('/dashboard/restrictive-lists-info').then(res => res.data),

  trainModel: (): Promise<{ status: string; message: string; candidates: number; started_at: string }> =>
    apiClient.post('/dashboard/model/train').then(res => res.data),

  getTrainingStatus: (): Promise<{ status: string; result?: any; error?: string; started_at?: string }> =>
    apiClient.get('/dashboard/model/train/status').then(res => res.data),

  // Health check
  healthCheck: (): Promise<{ status: string; timestamp: string; checks: any }> =>
    axios.get('/api/health').then(res => res.data),
};

export const systemAPI = {
  health: (): Promise<HealthResponse> => axios.get('/api/health').then((res) => res.data),
  ready: (): Promise<{ status: string }> => axios.get('/api/ready').then((res) => res.data),
  live: (): Promise<{ status: string }> => axios.get('/api/live').then((res) => res.data),
};

export const profilingAPI = {
  trigger: (payload: ProfilingTriggerPayload): Promise<ProfilingStatusResponse> =>
    apiClient.post('/profiling/trigger', payload).then((res) => res.data),

  analyze: (payload: ProfilingAnalyzePayload): Promise<ProfilingResultResponse> =>
    apiClient.post('/profiling/analyze', payload).then((res) => res.data),

  analyzeWithDocs: (payload: ProfilingAnalyzeWithDocsPayload): Promise<ProfilingResultResponse> =>
    apiClient.post('/profiling/analyze', payload, { timeout: 120000 }).then((res) => res.data),

  getResult: (profilingId: string): Promise<ProfilingResultResponse> =>
    apiClient.get(`/profiling/${profilingId}`).then((res) => res.data),

  getStatus: (profilingId: string): Promise<ProfilingStatusResponse> =>
    apiClient.get(`/profiling/status/${profilingId}`).then((res) => res.data),
};

export const reviewAPI = {
  getPending: (limit: number = 50, offset: number = 0): Promise<PendingReviewItem[]> =>
    apiClient.get(`/review/pending?limit=${limit}&offset=${offset}`).then((res) => res.data),

  submitReview: (
    profilingId: string,
    payload: AnalystReviewPayload,
  ): Promise<ProfilingResultResponse> => apiClient.put(`/review/${profilingId}`, payload).then((res) => res.data),
};

// ── OCR ───────────────────────────────────────────────────────────────────────

export interface OcrDocument {
  filename: string;
  base64: string;
  content_type: string;
}

export interface OcrAnalyzePayload {
  cc_number?: string;
  simulation_id?: string;
  documents: OcrDocument[];
}

export interface OcrIdentity {
  nombre_completo: string | null;
  numero_documento: string | null;
  fecha_nacimiento: string | null;
  lugar_expedicion: string | null;
  extraction_method: string;
}

export interface OcrIncome {
  entidad: string | null;
  nombre_titular: string | null;
  numero_documento: string | null;
  valor_base: number | null;
  neto_mensual: number | null;
  capacidad_endeudamiento: number | null;
  estado_activo: boolean | null;
  periodo: string | null;
  pagaduria_detected: string | null;
  extraction_method: string;
  nivel_contratacion: string | null;
  embargos: string[];
  descuentos: {
    aportes: number | null;
    afiliaciones: number | null;
    libranzas: number | null;
    otros: number | null;
  };
}

export interface OcrCrossValidation {
  cc_match: boolean | null;
  name_match: boolean | null;
}

export interface OcrResult {
  success: boolean;
  cc_number: string | null;
  simulation_id: string | null;
  identity: OcrIdentity | null;
  income: OcrIncome | null;
  cross_validation: OcrCrossValidation;
  documents_processed: number;
  errors: string[];
}

export const ocrAPI = {
  analyze: (payload: OcrAnalyzePayload): Promise<OcrResult> =>
    apiClient.post('/ocr/analyze', payload, { timeout: 90000 }).then(res => res.data),
};

export default apiClient;